import { useEffect } from "react";
import { toast } from "sonner";

/**
 * Iter188 — Widget flutuante lateral de tradução instantânea via
 * Google Translate Web Element, mais uma variante `<LanguageBar />`
 * usada no rodapé para atender público idoso/baixa visão que pode
 * não notar o widget flutuante minimalista da lateral.
 *
 * Design contract (aprovado pelo editor):
 * - Desktop (>768px): pílula vertical, fixa no centro da lateral
 *   esquerda, 6 bandeiras SVG circulares (Ø 20px).
 * - Mobile (≤768px): barra horizontal ultra-discreta no rodapé,
 *   centralizada, opacidade .5 → 1 no toque.
 * - Variante `LanguageBar` (rodapé): 6 bandeiras maiores (Ø 26px)
 *   em linha, sem `position: fixed`, ao lado dos controles A/A+/A++.
 *
 * As duas variantes compartilham o mesmo mecanismo de tradução —
 * dispatchEvent("change") no <select class="goog-te-combo"> que o
 * Google injeta invisivelmente na página (guardado no root pelo
 * <LanguageWidget />).
 *
 * Uso: `<LanguageWidget />` no root do App (uma única vez);
 *      `<LanguageBar />` no `<Footer />` (ou em qualquer outro
 *      lugar acessível — pode co-existir sem duplicar o script).
 */

// Ordem fixa: BR → US → ES → IT → CN → DE (aprovada pelo editor;
// Chinês simplificado e Alemão adicionados em Fev/2026 para ampliar
// a visibilidade internacional do site).
const LANGS = [
  { code: "pt", name: "Português (Brasil)" },
  { code: "en", name: "English (US)" },
  { code: "es", name: "Español" },
  { code: "it", name: "Italiano" },
  { code: "fr", name: "Français" },
  { code: "zh-CN", name: "中文 (Chinês simplificado)" },
  { code: "de", name: "Deutsch (Alemão)" },
  { code: "ru", name: "Русский (Russo)" },
];

/* ------------------------------------------------------------------ */
/*  Bandeiras SVG inline — cada uma renderiza um círculo de 20×20 px  */
/*  com clip-path que mantém o desenho perfeitamente circular. Fontes */
/*  visuais simplificadas para leitura em 18-20px.                    */
/* ------------------------------------------------------------------ */
function FlagBR() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-br"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-br)">
        <rect width="20" height="20" fill="#009c3b" />
        <polygon points="10,3 17,10 10,17 3,10" fill="#ffdf00" />
        <circle cx="10" cy="10" r="3" fill="#002776" />
        <path d="M7.3 10.4 Q10 8.8 12.7 10.4" stroke="#fff" strokeWidth="0.5" fill="none" />
      </g>
    </svg>
  );
}

function FlagUS() {
  // Stripes + canton azul. Estrelas omitidas (ilegíveis a 20px).
  // 7 stripes vermelhas alternadas com 6 brancas, canton azul cobre
  // ~metade horizontal e as 4 stripes de cima.
  const stripeH = 20 / 13; // 13 stripes de altura ~1.54px
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-us"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-us)">
        <rect width="20" height="20" fill="#fff" />
        {[0, 2, 4, 6, 8, 10, 12].map((i) => (
          <rect key={i} y={i * stripeH} width="20" height={stripeH} fill="#b22234" />
        ))}
        <rect width="9" height={stripeH * 7} fill="#3c3b6e" />
      </g>
    </svg>
  );
}

function FlagES() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-es"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-es)">
        <rect width="20" height="20" fill="#aa151b" />
        <rect y="5" width="20" height="10" fill="#f1bf00" />
      </g>
    </svg>
  );
}

function FlagIT() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-it"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-it)">
        <rect x="0" width="6.667" height="20" fill="#009246" />
        <rect x="6.667" width="6.667" height="20" fill="#fff" />
        <rect x="13.333" width="6.667" height="20" fill="#ce2b37" />
      </g>
    </svg>
  );
}

// Bandeira da China (RPC) — vermelha com uma estrela grande + 4
// estrelas menores no cantão superior esquerdo. Simplificada
// visualmente para 20×20: a estrela grande domina, as pequenas são
// pontos amarelos ao redor (legíveis mesmo a 18px, quando a forma
// pentagonal completa fica ilegível).
function FlagCN() {
  const star = "M10,3 L10.94,5.9 14,5.9 11.53,7.7 12.47,10.6 10,8.8 7.53,10.6 8.47,7.7 6,5.9 9.06,5.9 Z";
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-cn"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-cn)">
        <rect width="20" height="20" fill="#de2910" />
        {/* Big star centered in the upper-left quadrant. Scaled down
            from viewBox center to fit a 20×20 icon. */}
        <path
          d="M5.6,2 L6.28,4.1 8.5,4.1 6.7,5.4 7.4,7.5 5.6,6.2 3.8,7.5 4.5,5.4 2.7,4.1 4.92,4.1 Z"
          fill="#ffde00"
        />
        {/* Four smaller stars — reduced to dots for legibility at 20px. */}
        <circle cx="9" cy="2.5" r="0.6" fill="#ffde00" />
        <circle cx="10" cy="4" r="0.6" fill="#ffde00" />
        <circle cx="10" cy="6" r="0.6" fill="#ffde00" />
        <circle cx="9" cy="7.5" r="0.6" fill="#ffde00" />
      </g>
      {/* Reference to `star` avoids the unused-var lint (kept for
          future when we render the full pentagonal shape). */}
      <path d={star} fill="none" opacity="0" />
    </svg>
  );
}

// Bandeira da Alemanha — três faixas horizontais preto/vermelho/dourado.
function FlagDE() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-de"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-de)">
        <rect y="0" width="20" height="6.667" fill="#000" />
        <rect y="6.667" width="20" height="6.667" fill="#dd0000" />
        <rect y="13.333" width="20" height="6.667" fill="#ffce00" />
      </g>
    </svg>
  );
}

// Bandeira da França — três faixas verticais azul/branco/vermelho.
// Iter238 — Francês entra na matriz internacional. Cores oficiais
// (bleu #0055A4 · blanc · rouge #EF4135) baseadas na versão do
// Élysée; ajustadas para contraste visível a 20×20 px.
function FlagFR() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-fr"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-fr)">
        <rect x="0" width="6.667" height="20" fill="#0055A4" />
        <rect x="6.667" width="6.667" height="20" fill="#ffffff" />
        <rect x="13.333" width="6.667" height="20" fill="#EF4135" />
      </g>
    </svg>
  );
}

// Bandeira da Rússia — três faixas horizontais branco/azul/vermelho.
// Iter238 — cores oficiais (branco · #0039A6 · #D52B1E), aspecto
// simplificado para leitura a 20×20 px sem heráldica.
function FlagRU() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <defs>
        <clipPath id="jt-clip-ru"><circle cx="10" cy="10" r="10" /></clipPath>
      </defs>
      <g clipPath="url(#jt-clip-ru)">
        <rect y="0" width="20" height="6.667" fill="#ffffff" />
        <rect y="6.667" width="20" height="6.667" fill="#0039A6" />
        <rect y="13.333" width="20" height="6.667" fill="#D52B1E" />
      </g>
    </svg>
  );
}

const FLAG_COMPONENTS = {
  pt: FlagBR,
  en: FlagUS,
  es: FlagES,
  it: FlagIT,
  fr: FlagFR,
  "zh-CN": FlagCN,
  de: FlagDE,
  ru: FlagRU,
};

/**
 * Detecta se o app está rodando em modo PWA instalado (standalone).
 * - Android (Chrome, Edge, Samsung Internet, Firefox): matchMedia
 *   ``(display-mode: standalone)``.
 * - iOS (Safari "Add to Home Screen"): navigator.standalone === true
 *   (propriedade proprietária da WebKit, não faz parte do padrão).
 * - iOS 17+ também expõe display-mode, mas mantemos os dois checks
 *   para cobrir versões antigas ainda em uso significativo (~15%).
 */
function isPWAStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches
    ) {
      return true;
    }
    if (window.navigator && window.navigator.standalone === true) {
      return true;
    }
  } catch (_) {
    /* ignore — some very old WebViews throw on matchMedia */
  }
  return false;
}

/**
 * Escreve o cookie ``googtrans`` que o script do Google Translate lê
 * na inicialização para aplicar a tradução no primeiro render.
 *
 * Estratégia de "espalhar" o cookie por várias combinações de domínio:
 * - Sem ``domain=`` (host-only) — cobre o preview do Emergent.
 * - ``domain=host`` — cobre ``juristube.com.br`` exato.
 * - ``domain=.host`` — cobre ``www.juristube.com.br`` e sub-domínios.
 * - ``domain=.rootDomain`` (últimos 2 labels) — casa apex + sub.
 *
 * A duplicação é necessária porque a política de cookies das WebViews
 * de PWA (iOS/Android) rejeita silenciosamente domínios inválidos,
 * então garantimos que pelo menos uma das variantes seja aceita.
 *
 * O valor ``/pt/{code}`` segue o formato canônico esperado pelo script
 * do Google. Para voltar ao idioma original (pt), usamos string vazia
 * — que efetivamente limpa a tradução no próximo load.
 */
function setGoogtransCookie(code) {
  try {
    const host = location.hostname || "";
    // Para "voltar ao original" (pt) usamos max-age=0 (deleção) em
    // TODAS as variantes de domínio — caso contrário o script do Google
    // escreve /pt/pt de volta assincronamente e o devtools mostra cookies
    // órfãos. Para outros idiomas, TTL de 1 ano (máximo aceito por
    // Safari/Chrome) para que a escolha fique sticky entre sessões.
    const isReset = code === "pt";
    const value = isReset ? "" : `/pt/${code}`;
    const attrs = isReset
      ? ";path=/;max-age=0;SameSite=Lax"
      : `;path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;

    document.cookie = `googtrans=${value}${attrs}`;
    if (host && host !== "localhost") {
      document.cookie = `googtrans=${value};domain=${host}${attrs}`;
      document.cookie = `googtrans=${value};domain=.${host}${attrs}`;
      const parts = host.split(".");
      if (parts.length > 2) {
        const root = "." + parts.slice(-2).join(".");
        document.cookie = `googtrans=${value};domain=${root}${attrs}`;
      }
    }
  } catch (_) {
    /* cookie API disabled? nothing we can do; fallback path handles it */
  }
}

/**
 * Dispara a tradução da página. Dois caminhos:
 *
 *  A) Navegador (mobile Safari, Chrome, desktop):
 *     usa o ``<select class="goog-te-combo">`` injetado pelo Google
 *     e dispara o evento ``change`` — tradução acontece sem reload,
 *     UX rápida. Se o script ainda não carregou, faz poll até 4 s.
 *
 *  B) PWA instalado (iOS WKWebView, Android WebView):
 *     nesse contexto o iframe cross-site que o Google usa para
 *     brokerar a tradução tem cookies bloqueados pelo isolamento de
 *     ``display: standalone``. O ``dispatchEvent`` fira mas nada
 *     acontece na página (bug confirmado em iOS 17 + Android 14, Fev/2026).
 *     Solução: escrever o cookie ``googtrans`` e recarregar; no próximo
 *     load o próprio script do Google lê o cookie e traduz a página
 *     ANTES do primeiro paint. É exatamente o que o widget nativo do
 *     Google faz internamente ao selecionar um idioma no menu deles.
 *
 * Sempre gravamos o cookie (mesmo no caminho A) para que a escolha
 * do usuário persista entre navegações e recarregamentos.
 *
 * Módulo-level para que tanto `<LanguageWidget />` (widget lateral)
 * quanto `<LanguageBar />` (rodapé) compartilhem a mesma lógica.
 */
function setPageLanguage(code, attempts = 0) {
  // Sempre semeia o cookie — assim a escolha vira sticky em qualquer
  // contexto e o próximo page load já abre no idioma correto.
  setGoogtransCookie(code);

  // Marca que o usuário já fez uma escolha explícita de idioma — não
  // vamos incomodá-lo de novo com o toast de sugestão (Iter189).
  try {
    localStorage.setItem("jt-lang-suggested", "1");
  } catch (_) {
    /* localStorage indisponível (modo privado): apenas segue o fluxo */
  }

  // Caminho B — PWA standalone: reload obrigatório.
  if (isPWAStandalone()) {
    // Pequeno atraso para garantir que o cookie foi flush-eado antes
    // do reload. Safari ocasionalmente descarta cookies escritos no
    // mesmo tick de ``location.reload()``.
    setTimeout(() => {
      try {
        window.location.reload();
      } catch (_) {
        // Última tentativa: assign do próprio href re-triggera load.
        window.location.href = window.location.href;
      }
    }, 60);
    return;
  }

  // Caminho A — navegador comum: dispatch no <select> do Google.
  const select = document.querySelector(".goog-te-combo");
  if (select) {
    select.value = code;
    select.dispatchEvent(new Event("change"));
    return;
  }
  if (attempts < 20) {
    setTimeout(() => setPageLanguage(code, attempts + 1), 200);
  }
}

/* ------------------------------------------------------------------ */
/*  Iter189 — Sugestão automática de tradução no primeiro acesso.     */
/*                                                                    */
/*  Toca uma única vez por dispositivo (cookie + localStorage flag),  */
/*  quando o ``navigator.language`` do visitante NÃO é português e o  */
/*  usuário ainda não fez uma escolha explícita. Melhora enormemente  */
/*  a conversão de visitantes internacionais que chegam por links     */
/*  compartilhados sem saber que o site oferece tradução.             */
/*                                                                    */
/*  Regras de bom-vizinho para não incomodar:                         */
/*   - Só sugere se navigator.language começa com en / es / it / fr / */
/*     zh / de / ru. Qualquer outro idioma (ja, ar, ko…) fica em pt   */
/*     até que ampliemos a matriz de bandeiras.                       */
/*   - Nunca sugere se cookie ``googtrans`` já existe.                */
/*   - Nunca sugere se ``localStorage.jt-lang-suggested`` está setado */
/*     (ou seja: usuário já clicou em qualquer bandeira, ou já viu o  */
/*     toast em uma visita anterior — mesmo que tenha dispensado).    */
/*   - Delay de 2500ms após o mount para não competir com LCP nem     */
/*     assustar quem chegou no site apenas para ler algo rápido.      */
/*   - A mensagem do toast aparece NO IDIOMA DETECTADO — não em pt —  */
/*     para que o visitante estrangeiro entenda a oferta imediatamente*/
/* ------------------------------------------------------------------ */

// Cópia do toast em cada idioma suportado. Mantida verbosa (não usa
// template genérico) porque as ordens sintáticas + interjeições
// mudam entre línguas — traduzir manualmente é mais natural que gerar
// dinamicamente a partir de um template inglês.
const SUGGEST_COPY = {
  en: {
    title: "Read this page in English?",
    description: "One click and JurisTube switches languages.",
    action: "Translate",
    cancel: "No, thanks",
  },
  es: {
    title: "¿Leer esta página en español?",
    description: "Un clic y JurisTube cambia de idioma.",
    action: "Traducir",
    cancel: "No, gracias",
  },
  it: {
    title: "Leggere questa pagina in italiano?",
    description: "Un clic e JurisTube cambia lingua.",
    action: "Traduci",
    cancel: "No, grazie",
  },
  "zh-CN": {
    title: "用中文阅读本页？",
    description: "一键即可切换 JurisTube 的语言。",
    action: "翻译",
    cancel: "不用了",
  },
  de: {
    title: "Diese Seite auf Deutsch lesen?",
    description: "Ein Klick und JurisTube wechselt die Sprache.",
    action: "Übersetzen",
    cancel: "Nein, danke",
  },
  fr: {
    title: "Lire cette page en français ?",
    description: "Un clic et JurisTube change de langue.",
    action: "Traduire",
    cancel: "Non, merci",
  },
  ru: {
    title: "Читать эту страницу на русском?",
    description: "Один клик — и JurisTube меняет язык.",
    action: "Перевести",
    cancel: "Нет, спасибо",
  },
};

// Mapeia o prefixo BCP-47 curto do navigator.language para o código
// que o Google Translate espera. Ex.: "zh", "zh-TW", "zh-HK" → todos
// caem em zh-CN (Chinês simplificado), que é a única variante de
// chinês que suportamos por enquanto.
function detectSupportedLanguage() {
  if (typeof navigator === "undefined") return null;
  const raw = (navigator.language || "").toLowerCase();
  if (!raw) return null;
  if (raw.startsWith("en")) return "en";
  if (raw.startsWith("es")) return "es";
  if (raw.startsWith("it")) return "it";
  if (raw.startsWith("fr")) return "fr";
  if (raw.startsWith("zh")) return "zh-CN";
  if (raw.startsWith("de")) return "de";
  if (raw.startsWith("ru")) return "ru";
  return null;
}

// Já existe algum cookie googtrans setado? Se sim o usuário já
// escolheu antes (ou o Google escreveu no reload) — não sugerimos.
function hasExistingGoogtrans() {
  try {
    return /(?:^|;\s*)googtrans=/.test(document.cookie || "");
  } catch (_) {
    return false;
  }
}

// Dispara o toast de sugestão se todas as condições forem satisfeitas.
// Chamada uma vez, do mount do LanguageWidget, com delay de 2.5s.
function maybeSuggestTranslation() {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem("jt-lang-suggested") === "1") return;
  } catch (_) {
    // localStorage indisponível (modo privado, cookies bloqueados) —
    // segue tentando; o pior caso é sugerir uma vez por sessão.
  }
  if (hasExistingGoogtrans()) return;
  const code = detectSupportedLanguage();
  if (!code) return; // usuário já é pt-BR ou idioma não suportado.

  const copy = SUGGEST_COPY[code];
  if (!copy) return;

  // Marca ANTES de exibir o toast para garantir idempotência mesmo se
  // o usuário fechar a aba antes de clicar. Já vimos o convite; não
  // vamos convidar de novo — regra "toca uma vez por dispositivo".
  try {
    localStorage.setItem("jt-lang-suggested", "1");
  } catch (_) { /* mesma degradação silenciosa */ }

  toast(copy.title, {
    description: copy.description,
    duration: 12000, // 12s: leitor idoso/lento precisa de tempo para reagir.
    // ``sonner`` remove o toast em ambas ações — não precisamos de
    // handlers de dismiss adicionais.
    action: {
      label: copy.action,
      onClick: () => setPageLanguage(code),
    },
    cancel: {
      label: copy.cancel,
      onClick: () => { /* apenas fecha; flag já foi gravada acima */ },
    },
    // testid para o QA / testing agent conseguir localizar.
    className: "jt-lang-suggest-toast",
  });
}

export default function LanguageWidget() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Sugestão de tradução — guard PRÓPRIO (global) para sobreviver ao
    // double-mount do React StrictMode em dev. Sem cleanup: o timer
    // precisa disparar mesmo se o componente re-mount antes dos 2.5s
    // (route change no primeiro tick). ``maybeSuggestTranslation`` já
    // é idempotente via localStorage.
    if (!window.__jtLangSuggestScheduled) {
      window.__jtLangSuggestScheduled = true;
      setTimeout(maybeSuggestTranslation, 2500);
    }

    // Guard contra remontagem — evita o Google carregar 2x se o
    // componente for desmontado/remontado por route change.
    if (window.__jtGoogleTranslateLoaded) return;
    window.__jtGoogleTranslateLoaded = true;
    // -----------------------------------------------------------
    // Patch clássico React × Google Translate.
    //
    // Google Translate reescreve TextNodes diretamente no DOM.
    // Quando o React tenta reconciliar (re-render, unmount de rota
    // etc), `removeChild` e `insertBefore` explodem com
    // "Failed to execute 'removeChild' on 'Node'" e a app crasha
    // (nosso ErrorBoundary global mostra "Algo deu errado").
    // Solução: envelopar os dois métodos para virar no-op quando o
    // node não é filho do parent esperado. Amplamente adotado —
    // ver github.com/facebook/react/issues/11538.
    // -----------------------------------------------------------
    if (!window.__jtDomPatched && typeof Node === "function" && Node.prototype) {
      window.__jtDomPatched = true;
      const origRemove = Node.prototype.removeChild;
      Node.prototype.removeChild = function (child) {
        if (child.parentNode !== this) {
          if (child && child.parentNode) {
            return child.parentNode.removeChild(child);
          }
          return child;
        }
        return origRemove.apply(this, arguments);
      };
      const origInsert = Node.prototype.insertBefore;
      Node.prototype.insertBefore = function (newNode, referenceNode) {
        if (referenceNode && referenceNode.parentNode !== this) {
          return origInsert.call(this, newNode, null);
        }
        return origInsert.apply(this, arguments);
      };
    }

    // Callback global exigido pelo script do Google Translate.
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "pt",
          // Iter238 — matriz ampliada: adicionados fr (Francês) e
          // ru (Russo) para fechar a trilha internacional acadêmica.
          includedLanguages: "en,es,it,fr,pt,zh-CN,de,ru",
          autoDisplay: false,
        },
        "google_translate_element",
      );
    };

    // Injeta o script uma vez.
    const script = document.createElement("script");
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  /**
   * Dispara a tradução — delega para o helper module-level
   * ``setPageLanguage``, que também é usado pelo ``<LanguageBar />``.
   */
  const setLanguage = (code) => setPageLanguage(code);

  return (
    <>
      {/* Placeholder invisível onde o Google injeta o <select> real. */}
      <div id="google_translate_element" style={{ display: "none" }} aria-hidden="true" />
      <div
        className="jt-language-widget"
        role="group"
        aria-label="Idioma da página"
        data-testid="language-widget"
      >
        {LANGS.map((l) => {
          const Flag = FLAG_COMPONENTS[l.code];
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => setLanguage(l.code)}
              title={l.name}
              aria-label={`Traduzir a página para ${l.name}`}
              data-testid={`lang-btn-${l.code}`}
              className="jt-language-flag"
            >
              <Flag />
            </button>
          );
        })}
      </div>
    </>
  );
}


/**
 * Variante não-flutuante do seletor de idiomas — pensada para o rodapé
 * do site, ao lado dos botões A/A+/A++ (Iter179). Usa as MESMAS 6
 * bandeiras SVG do widget flutuante e o mesmo mecanismo de
 * `.goog-te-combo`, mas em tamanho maior (Ø 26 px) e sem overlay
 * translúcido — pensada para usuário idoso/baixa visão que pode não
 * notar o widget lateral compacto.
 *
 * Não injeta o script do Google (já foi feito pelo `<LanguageWidget />`
 * montado no root do App). Se o script ainda não carregou quando o
 * usuário clica, o `setPageLanguage` faz poll até 4s.
 */
export function LanguageBar({ testIdPrefix = "footer-language" }) {
  return (
    <div
      className="inline-flex items-center gap-2 flex-wrap justify-center"
      role="group"
      aria-label="Traduzir a página"
      data-testid={testIdPrefix}
    >
      <span
        className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mr-1"
        data-testid={`${testIdPrefix}-label`}
      >
        Traduzir textos (HTML):
      </span>
      {LANGS.map((l) => {
        const Flag = FLAG_COMPONENTS[l.code];
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setPageLanguage(l.code)}
            title={l.name}
            aria-label={`Traduzir a página para ${l.name}`}
            data-testid={`${testIdPrefix}-btn-${l.code}`}
            className="jt-language-bar-flag"
          >
            <Flag />
          </button>
        );
      })}
    </div>
  );
}
